import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import DashboardLayout from '../../components/DashboardLayout'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import { Textarea } from '../../components/ui/textarea'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog'
import { toast } from 'sonner'
import { MessageSquare, MapPin, Euro, AlertCircle, Send } from 'lucide-react'
import { applicationsService, postsService } from '../../services/api'

const urgencyLabels = {
  normale: { label: 'Normale', color: 'bg-blue-100 text-blue-800' },
  urgente: { label: 'Urgent', color: 'bg-orange-100 text-orange-800' },
  tres_urgente: { label: 'Très urgent', color: 'bg-red-100 text-red-800' },
}

const parseSalaryRange = (salaryRange) => {
  if (!salaryRange) return {}
  try {
    const parsed = JSON.parse(salaryRange)
    if (typeof parsed === 'object' && parsed !== null) return parsed
  } catch {
    return { label: salaryRange }
  }
  return {}
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

const formatBudget = (salaryRange) => {
  const budget = parseSalaryRange(salaryRange)
  if (budget.label) return budget.label
  if (budget.min != null && budget.max != null) {
    return `${Number(budget.min).toLocaleString('fr-FR')} – ${Number(budget.max).toLocaleString('fr-FR')} €`
  }
  if (budget.max != null) return `Jusqu'à ${Number(budget.max).toLocaleString('fr-FR')} €`
  if (budget.min != null) return `À partir de ${Number(budget.min).toLocaleString('fr-FR')} €`
  return null
}

const buildResponseMessage = (data) => {
  const parts = [data.message.trim()]
  if (data.estimation_budget) {
    parts.push(`Budget estimé : ${data.estimation_budget} €`)
  }
  if (data.delai_estime) {
    parts.push(`Délai estimé : ${data.delai_estime}`)
  }
  if (data.disponibilite) {
    parts.push(`Disponibilité : ${data.disponibilite}`)
  }
  return parts.filter(Boolean).join('\n\n')
}

export default function RespondRequests() {
  const { hasModule, loading: authLoading } = useAuth()
  const [requests, setRequests] = useState([])
  const [myResponses, setMyResponses] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [responseData, setResponseData] = useState({
    message: '',
    estimation_budget: '',
    delai_estime: '',
    disponibilite: '',
  })

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [requestsRes, responsesRes] = await Promise.all([
        postsService.getPosts({ type: 'REQUEST', status: 'PUBLISHED', limit: 50 }),
        applicationsService.getSentApplications({ limit: 100 }),
      ])

      const requestsData = requestsRes?.data ?? requestsRes ?? []
      setRequests(Array.isArray(requestsData) ? requestsData : [])

      const responsesData = responsesRes?.data ?? responsesRes ?? []
      setMyResponses(Array.isArray(responsesData) ? responsesData : [])
    } catch (error) {
      console.error('[RespondRequests] fetch error:', error)
      toast.error(error?.message || 'Impossible de charger les demandes')
      setRequests([])
      setMyResponses([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (authLoading) return
    fetchData()
  }, [authLoading, fetchData])

  const hasResponded = (requestId) =>
    myResponses.some((response) => response.postId === requestId)

  const openResponseDialog = (request) => {
    setSelectedRequest(request)
    setResponseData({
      message: '',
      estimation_budget: '',
      delai_estime: '',
      disponibilite: '',
    })
    setDialogOpen(true)
  }

  const handleSubmitResponse = async (e) => {
    e.preventDefault()
    if (!selectedRequest?.id) return

    setSending(true)
    try {
      await applicationsService.apply(
        selectedRequest.id,
        buildResponseMessage(responseData)
      )

      toast.success('Réponse envoyée avec succès')
      setDialogOpen(false)
      await fetchData()
    } catch (error) {
      toast.error(error?.message || 'Erreur lors de l\'envoi')
    } finally {
      setSending(false)
    }
  }

  if (!hasModule('request_response_access')) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-center py-12">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Accès restreint</h2>
          <p className="text-slate-600 mb-4">
            Vous devez avoir l&apos;accès au module &quot;Répondre aux demandes&quot; pour proposer vos services.
          </p>
          <Link to="/tableau-de-bord/demandes-acces">
            <Button>Demander l&apos;accès</Button>
          </Link>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="w-full">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Répondre aux demandes</h1>
          <p className="text-slate-600">Consultez les demandes et proposez vos services</p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="lux-dashboard-card p-6 animate-pulse">
                <div className="h-5 bg-slate-200 rounded w-1/4 mb-4" />
                <div className="h-6 bg-slate-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-slate-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : requests.length === 0 ? (
          <div className="lux-dashboard-card p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucune demande disponible</h3>
            <p className="text-slate-600">Revenez plus tard pour voir les nouvelles demandes</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => {
              const responded = hasResponded(request.id)
              const budgetLabel = formatBudget(request.salaryRange)
              const meta = parseSalaryRange(request.salaryRange)
              const urgency = meta.urgence ? urgencyLabels[meta.urgence] : null
              const title = request.title ?? request.titre ?? 'Sans titre'
              const location = request.locationCity ?? request.localisation ?? 'Non renseignée'
              const categoryName = request.category?.name ?? request.categorie
              const description = request.description?.trim()
                ? request.description
                : 'Aucune description fournie.'

              return (
                <div
                  key={request.id}
                  className="lux-dashboard-card p-6"
                  data-testid={`request-${request.id}`}
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {categoryName && <Badge variant="outline">{categoryName}</Badge>}
                      {urgency && (
                        <Badge className={urgency.color}>
                          <AlertCircle className="w-3 h-3 mr-1" />
                          {urgency.label}
                        </Badge>
                      )}
                      {responded && <Badge className="badge-new">Répondu</Badge>}
                    </div>
                    <span className="text-sm text-slate-500 whitespace-nowrap">
                      {formatDate(request.createdAt ?? request.created_at)}
                    </span>
                  </div>

                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
                  <p className="text-slate-600 mb-4 line-clamp-2">{description}</p>

                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {location}
                      </span>
                      {budgetLabel && (
                        <span className="flex items-center gap-1">
                          <Euro className="w-4 h-4" />
                          {budgetLabel}
                        </span>
                      )}
                    </div>

                    <Button
                      onClick={() => openResponseDialog(request)}
                      disabled={responded}
                      data-testid={`respond-${request.id}`}
                    >
                      {responded ? 'Déjà répondu' : 'Répondre'}
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Répondre à la demande</DialogTitle>
            </DialogHeader>

            {selectedRequest && (
              <form onSubmit={handleSubmitResponse} className="space-y-4 mt-4">
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-slate-900 mb-1">
                    {selectedRequest.title ?? selectedRequest.titre}
                  </h4>
                  <p className="text-sm text-slate-600">
                    {selectedRequest.locationCity ?? selectedRequest.localisation}
                  </p>
                </div>

                <div>
                  <Label className="form-label">Votre message *</Label>
                  <Textarea
                    required
                    rows={4}
                    value={responseData.message}
                    onChange={(e) => setResponseData({ ...responseData, message: e.target.value })}
                    placeholder="Présentez votre proposition..."
                    data-testid="response-message"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">Estimation budget (€)</Label>
                    <Input
                      type="number"
                      value={responseData.estimation_budget}
                      onChange={(e) => setResponseData({ ...responseData, estimation_budget: e.target.value })}
                      placeholder="1500"
                      data-testid="response-budget"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Délai estimé</Label>
                    <Input
                      value={responseData.delai_estime}
                      onChange={(e) => setResponseData({ ...responseData, delai_estime: e.target.value })}
                      placeholder="2 semaines"
                      data-testid="response-delai"
                    />
                  </div>
                </div>

                <div>
                  <Label className="form-label">Votre disponibilité</Label>
                  <Input
                    value={responseData.disponibilite}
                    onChange={(e) => setResponseData({ ...responseData, disponibilite: e.target.value })}
                    placeholder="Disponible dès maintenant"
                    data-testid="response-disponibilite"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" disabled={sending} data-testid="submit-response">
                    <Send className="w-4 h-4 mr-2" />
                    {sending ? 'Envoi...' : 'Envoyer la réponse'}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  )
}
